import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { finalize, tap } from 'rxjs';

import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import {
  DialogBase,
  TiimeButtonModule,
  TiimeChipsModule,
  TiimeDialogModule,
  TiimeFormModule,
  TiimeInputContainerModule,
  TiimeSelectModule,
  TiimeSnackbarService,
  TiimeTooltipModule,
  TiimeOptionModule
} from 'tiime-material';

import { ContactSupportApiService } from '@api-services/contact-support-api.service';
import { CONTACT_SUPPORT_REASON_LABEL } from '@constants/contact-support-reason-label.constant';
import { ContactSupportForm } from '@forms/contact-support-form';
import { ArithmeticHelper } from '@helpers/arithmetic.helper';
import { FileHelper } from '@helpers/file.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { ContactSupport } from '@models/contact-support';
import { PieceFile } from '@models/piece-file';
import { companySelector } from '@modules/company/company-core/store/company/company-selector';
import { businessUserSelector } from '@modules/core/store/business-user/business-user-selector';
import { AddFilesButtonComponent } from '@shared-components/add-files-button/add-files-button.component';
import { AttachmentItemComponent } from '@shared-components/attachment-item/attachment-item.component';
import {
  extendedAcceptedFileTypes,
  UPLOAD_FILE_CONFIG
} from '@shared-overlays/upload-file-overlay/upload-file-config.constant';

import { ContactSupportDialogData } from './contact-support-dialog-data';

@Component({
  selector: 'app-contact-support-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TiimeDialogModule,
    TiimeFormModule,
    TiimeInputContainerModule,
    TiimeSelectModule,
    TiimeOptionModule,
    TextFieldModule,
    ReactiveFormsModule,
    TiimeButtonModule,
    AddFilesButtonComponent,
    TiimeTooltipModule,
    AttachmentItemComponent,
    TiimeChipsModule
  ],
  templateUrl: './contact-support-dialog.component.html',
  styleUrl: './contact-support-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactSupportDialogComponent extends DialogBase<ContactSupportDialogComponent> {
  protected readonly contactSupportForm: ContactSupportForm = new ContactSupportForm();
  protected readonly CONTACT_SUPPORT_REASON_LABEL: typeof CONTACT_SUPPORT_REASON_LABEL = CONTACT_SUPPORT_REASON_LABEL;
  protected readonly acceptedFileTpes: string[] = extendedAcceptedFileTypes;

  private readonly company: Signal<Company> = this.store.selectSignal<Company>(companySelector);
  private readonly businessUser: Signal<BusinessUser> = this.store.selectSignal<BusinessUser>(businessUserSelector);

  constructor(
    private readonly contactSupportApiService: ContactSupportApiService,
    private readonly snackbarService: TiimeSnackbarService,
    private readonly store: Store<AppStoreState>,
    public dialogRef: MatDialogRef<ContactSupportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: ContactSupportDialogData
  ) {
    super(dialogRef);

    if (data) {
      this.initForm(data);
    }
  }

  filesAdded(files: PieceFile[]): void {
    if (files?.length === 0) {
      return;
    }

    const currentFilesSize: number = this.contactSupportForm.files.controls.reduce(
      (acc: number, curr: FormControl<File>) => ArithmeticHelper.plus(acc, curr.value.size / 1048576),
      0
    );

    if (
      FileHelper.isFilesSizesSumGreaterThanSize(
        files.map((pieceFile: PieceFile) => pieceFile.file),
        UPLOAD_FILE_CONFIG.maximumSize,
        currentFilesSize
      )
    ) {
      this.snackbarService.openError(
        `La taille totale des fichiers ne doit pas dépasser ${UPLOAD_FILE_CONFIG.maximumSize}Mo.`
      );
    } else {
      files.forEach((pieceFile: PieceFile) =>
        this.contactSupportForm.files.push(new FormControl<File>(pieceFile.file))
      );
    }
  }

  removeFile(index: number): void {
    this.contactSupportForm.files.removeAt(index);
  }

  @MarkFormAsTouchedIfInvalid('contactSupportForm')
  send(): void {
    const contactSupport: ContactSupport = this.contactSupportForm.toContactSupport();

    if (this.businessUser()) {
      contactSupport.userId = this.businessUser().id;
    }

    if (this.company()) {
      contactSupport.companyId = this.company().id;
      contactSupport.companyName = this.company().name;
    }

    this.isLoading.set(true);

    this.contactSupportApiService
      .sendToSupport(contactSupport)
      .pipe(
        tap(() => this.snackbarService.openSuccess('Votre message a bien été envoyé')),
        finalize(() => {
          this.isLoading.set(false);
          this.close(null);
        })
      )
      .subscribe();
  }

  private initForm(data: ContactSupportDialogData): void {
    this.contactSupportForm.patchValue({
      reason: data.reason,
      subject: data.subject
    });
  }
}
